<template>
  <CustomModal
    title="Create Dashboard"
    width="34vw"
    v-model:visible="internalVisible"
  >
    <div :id="`${source}_create_dashboard_modal`" class="flex flex-col gap-6">
      <div id="name" class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Dashboard name</span>
        <a-input
          ref="nameInputRef"
          v-model:value="formData.name"
          class="input_medium"
          placeholder="Enter dashboard name"
          @keyup.enter="handleSubmit"
          :autofocus="true"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex flex-row items-center justify-end gap-2 w-full">
        <a-button
          class="secondary_medium"
          :disabled="creating"
          @click="handleCancel"
        >
          Cancel
        </a-button>

        <a-button
          class="primary_medium"
          :disabled="isActionButtonDisabled"
          :loading="creating"
          @click="handleSubmit"
        >
          {{ creating ? "Creating.." : "Create" }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, reactive, watch, computed, nextTick } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import { defaultDashboardName } from "@/src/constants/dashboardConstants.js";
import CustomModal from "@/src/components/CustomModal.vue";

const router = useRouter();

const props = defineProps({
  source: { type: String, required: true },
  visible: { type: Boolean, required: true },
  space: { type: Object, default: null },
});

const emit = defineEmits(["update:visible"]);

const creating = ref(false);
const internalVisible = ref(props.visible);
const nameInputRef = ref(null);

const formData = reactive({
  name: defaultDashboardName,
});

const isActionButtonDisabled = computed(() => {
  return !formData.name || !formData.name.trim();
});

const resetForm = () => {
  try {
    Object.assign(formData, {
      name: defaultDashboardName,
    });
    creating.value = false;
  } catch (error) {
    console.error("An error occurred while resetting form: ", error);
  }
};

const handleCancel = () => {
  try {
    internalVisible.value = false;
    resetForm();
  } catch (error) {
    console.error("An error occurred while closing modal: ", error);
  }
};

const handleSubmit = async () => {
  try {
    await toCreateDashboard();
  } catch (error) {
    console.error("An error occurred while handling submit: ", error);
  }
};

const toCreateDashboard = async () => {
  try {
    creating.value = true;

    await router.push({
      name: "dashboards-templates",
      query: {
        name: formData.name?.trim(),
      },
    });

    internalVisible.value = false;
    resetForm();
  } catch (error) {
    console.error("An error occurred while creating dashboard: ", error);
    message.error("An error occurred while creating a dashboard!");
  } finally {
    creating.value = false;
  }
};

watch(
  () => props.visible,
  async (newVal) => {
    internalVisible.value = newVal;
    if (newVal) {
      await nextTick();
      nameInputRef.value?.focus();
    }
  },
);

watch(
  () => internalVisible.value,
  (newVal) => {
    emit("update:visible", newVal);
    if (!newVal) {
      resetForm();
    }
  },
);
</script>
